import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../config/supabase';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/SupabaseAuthContext'; // Importar o hook de autenticação
import { useNavigate } from 'react-router-dom';

// Componentes internos da página
const PageHeader: React.FC<{ onNewRecharge: () => void }> = ({ onNewRecharge }) => (
  <div className="flex items-center justify-between mb-4">
    <div>
      <h1 className="text-2xl font-bold text-gray-900">Pedidos de Recarga</h1>
      <p className="text-sm text-gray-600">Acompanhe seus pagamentos e o crédito de tokens.</p>
    </div>
    <Button onClick={onNewRecharge} className="bg-indigo-600 hover:bg-indigo-700 text-white">
      Nova recarga
    </Button>
  </div>
);

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const styles: Record<string, string> = {
    approved: 'bg-green-100 text-green-700 border-green-200',
    authorized: 'bg-green-100 text-green-700 border-green-200',
    accredited: 'bg-green-100 text-green-700 border-green-200',
    pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    in_process: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    failure: 'bg-red-100 text-red-700 border-red-200',
    cancelled: 'bg-gray-100 text-gray-700 border-gray-200',
    refunded: 'bg-blue-100 text-blue-700 border-blue-200',
    charged_back: 'bg-blue-100 text-blue-700 border-blue-200',
  };
  const cls = styles[status] || 'bg-gray-100 text-gray-700 border-gray-200';
  return <span className={`inline-flex items-center px-2 py-1 rounded border text-xs font-medium ${cls}`}>{status}</span>;
};

const LoadingTable: React.FC = () => (
  <div className="animate-pulse">
    <div className="h-6 bg-gray-200 rounded w-1/3 mb-4" />
    <div className="overflow-x-auto">
      <table className="min-w-full bg-white border">
        <thead>
          <tr>
            {Array.from({ length: 7 }).map((_, i) => (
              <th key={i} className="py-2 px-4 border-b">
                <div className="h-4 bg-gray-200 rounded w-24" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 5 }).map((_, i) => (
            <tr key={i}>
              {Array.from({ length: 7 }).map((_, j) => (
                <td key={j} className="py-3 px-4 border-b">
                  <div className="h-4 bg-gray-200 rounded w-full" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const EmptyState: React.FC<{ onNewRecharge: () => void }> = ({ onNewRecharge }) => (
  <div className="text-center py-10">
    <h2 className="text-lg font-semibold text-gray-700">Você ainda não possui pedidos de recarga.</h2>
    <p className="text-sm text-gray-500 mt-1">Crie uma nova recarga para começar.</p>
    <Button onClick={onNewRecharge} className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white">Nova recarga</Button>
  </div>
);

const FiltersBar: React.FC<{
  status: string;
  onStatusChange: (s: string) => void;
  search: string;
  onSearchChange: (v: string) => void;
}> = ({ status, onStatusChange, search, onSearchChange }) => (
  <div className="flex flex-wrap gap-3 items-center mb-4">
    <div className="flex items-center gap-2">
      <label className="text-sm text-gray-600">Filtrar status:</label>
      <select
        value={status}
        onChange={(e) => onStatusChange(e.target.value)}
        className="border rounded px-2 py-1 text-sm"
      >
        <option value="all">Todos</option>
        <option value="approved">Aprovado</option>
        <option value="pending">Pendente</option>
        <option value="failure">Falhou</option>
        <option value="authorized">Autorizado</option>
        <option value="in_process">Em processamento</option>
      </select>
    </div>
    <div className="flex items-center gap-2">
      <label className="text-sm text-gray-600">Buscar:</label>
      <input
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder="ID do pagamento ou purchase_id"
        className="border rounded px-2 py-1 text-sm w-64"
      />
    </div>
  </div>
);

const RechargeOrders = () => {
  const { user } = useAuth(); // Obter o usuário logado
  const [payments, setPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [search, setSearch] = useState<string>('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPayments = async () => {
      if (!user) return; // Não fazer nada se o usuário não estiver logado

      try {
        setLoading(true);
        
        // Buscar intenções de compra pendentes
        const { data: intentsData, error: intentsError } = await supabase
          .from('purchase_intents')
          .select('*')
          .eq('user_uid', user.id)
          .eq('status', 'pending')
          .order('created_at', { ascending: false });

        if (intentsError) throw intentsError;

        // Buscar histórico no token_ledger (compras confirmadas)
        const { data: ledgerData, error: ledgerError } = await supabase
          .from('token_ledger')
          .select('*')
          .eq('user_uid', user.id)
          .eq('transaction_type', 'purchase')
          .order('created_at', { ascending: false })
          .limit(50);

        if (ledgerError) throw ledgerError;

        // Mapear intenções (pendentes)
        const mappedIntents = (intentsData || []).map((intent: any) => ({
          id: intent.id,
          payment_id: intent.purchase_id,
          purchase_id: intent.purchase_id,
          user_uid: intent.user_uid,
          reais_amount: intent.amount,
          token_amount: intent.tokens_to_credit,
          status: 'pending',
          created_at: intent.created_at,
          credit_status: 'pending',
          description: `Intenção de compra - ${intent.tokens_to_credit} tokens`
        }));

        // Mapear ledger (confirmados)
        const mappedLedger = (ledgerData || []).map((entry: any) => ({
          id: entry.id,
          payment_id: entry.payment_id || `LEDGER_${entry.id}`,
          purchase_id: entry.metadata?.external_reference || '-',
          user_uid: entry.user_uid,
          reais_amount: entry.metadata?.reais_amount || entry.payment_amount || 0,
          token_amount: entry.amount,
          status: 'approved',
          created_at: entry.created_at,
          credit_status: 'credited',
          description: entry.description || `Recarga de ${entry.amount} tokens`
        }));
        
        setPayments([...mappedIntents, ...mappedLedger]);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, []);

  const handleVerifyStatus = async (paymentId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('verify-payment-status', {
        body: { paymentId },
      });

      if (error) {
        throw error;
      }

      alert(`Resposta da verificação: ${JSON.stringify(data)}`);
      // Atualizar a lista de pagamentos após a verificação
      // Recarregar tudo
      const { data: intentsData } = await supabase
        .from('purchase_intents')
        .select('*')
        .eq('user_uid', user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      const { data: ledgerData } = await supabase
        .from('token_ledger')
        .select('*')
        .eq('user_uid', user.id)
        .eq('transaction_type', 'purchase')
        .order('created_at', { ascending: false })
        .limit(50);

      const mappedIntents = (intentsData || []).map((intent: any) => ({
        id: intent.id,
        payment_id: intent.purchase_id,
        purchase_id: intent.purchase_id,
        user_uid: intent.user_uid,
        reais_amount: intent.amount,
        token_amount: intent.tokens_to_credit,
        status: 'pending',
        created_at: intent.created_at,
        credit_status: 'pending',
        description: `Intenção de compra - ${intent.tokens_to_credit} tokens`
      }));

      const mappedLedger = (ledgerData || []).map((entry: any) => ({
        id: entry.id,
        payment_id: entry.payment_id || `LEDGER_${entry.id}`,
        purchase_id: entry.metadata?.external_reference || '-',
        user_uid: entry.user_uid,
        reais_amount: entry.payment_amount || 0,
        token_amount: entry.amount,
        status: 'approved',
        created_at: entry.created_at,
        credit_status: 'credited',
        description: entry.description || `Recarga de ${entry.amount} tokens`
      }));
      
      setPayments([...mappedIntents, ...mappedLedger]);

    } catch (err: any) {
      alert(`Erro ao verificar status: ${err.message}`);
    }
  };

  const onNewRecharge = () => {
    // Navegar para a página de compra/recarga (ajuste a rota se necessário)
    navigate('/tokens');
  };

  const filteredPayments = useMemo(() => {
    let list = payments;
    if (statusFilter !== 'all') {
      list = list.filter((p) => (p.status || '').toLowerCase() === statusFilter);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((p) =>
        String(p.payment_id || '').toLowerCase().includes(q) ||
        String(p.purchase_id || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [payments, statusFilter, search]);

  if (loading) {
    return (
      <div className="container mx-auto p-4">
        <PageHeader onNewRecharge={onNewRecharge} />
        <LoadingTable />
      </div>
    );
  }

  if (error) {
    return <div>Erro ao carregar pedidos: {error}</div>;
  }

  return (
    <div className="container mx-auto p-4 bg-gray-50 text-gray-900 min-h-screen">
      <PageHeader onNewRecharge={onNewRecharge} />
      <FiltersBar
        status={statusFilter}
        onStatusChange={setStatusFilter}
        search={search}
        onSearchChange={setSearch}
      />

      {filteredPayments.length === 0 ? (
        <EmptyState onNewRecharge={onNewRecharge} />
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border text-gray-900">
            <thead>
              <tr>
                <th className="py-2 px-4 border-b">ID do Pagamento</th>
                <th className="py-2 px-4 border-b">Purchase ID</th>
                <th className="py-2 px-4 border-b">Usuário</th>
                <th className="py-2 px-4 border-b">Valor (R$)</th>
                <th className="py-2 px-4 border-b">Tokens</th>
                <th className="py-2 px-4 border-b">Status</th>
                <th className="py-2 px-4 border-b">Crédito</th>
                <th className="py-2 px-4 border-b">Data</th>
                <th className="py-2 px-4 border-b">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredPayments.map((payment) => (
                <tr key={payment.id}>
                  <td className="py-2 px-4 border-b font-mono text-xs">{payment.payment_id}</td>
                  <td className="py-2 px-4 border-b font-mono text-xs">{payment.purchase_id || '-'}</td>
                  <td className="py-2 px-4 border-b">{payment.user_uid}</td>
                  <td className="py-2 px-4 border-b">{Number(payment.reais_amount || 0).toFixed(2)}</td>
                  <td className="py-2 px-4 border-b">{payment.token_amount}</td>
                  <td className="py-2 px-4 border-b"><StatusBadge status={(payment.status || '').toLowerCase()} /></td>
                  <td className="py-2 px-4 border-b">
                    {(() => {
                      const s = (((payment as any)?.credit_status) || '').toLowerCase();
                      if (s === 'credited') {
                        return <span className="inline-flex items-center px-2 py-1 rounded border text-xs font-medium bg-green-100 text-green-700 border-green-200">creditado</span>;
                      }
                      if (s === 'error') {
                        return (
                          <div className="flex flex-col">
                            <span className="inline-flex items-center px-2 py-1 rounded border text-xs font-medium bg-red-100 text-red-700 border-red-200">erro</span>
                            {((payment as any)?.credit_error) && (
                              <span className="text-xs text-red-600 mt-1 truncate max-w-[220px]" title={(payment as any).credit_error}>{(payment as any).credit_error}</span>
                            )}
                          </div>
                        );
                      }
                      // Padrão: pendente
                      return <span className="inline-flex items-center px-2 py-1 rounded border text-xs font-medium bg-yellow-100 text-yellow-700 border-yellow-200">pendente</span>;
                    })()}
                  </td>
                  <td className="py-2 px-4 border-b">{new Date(payment.created_at).toLocaleString()}</td>
                  <td className="py-2 px-4 border-b">
                    <div className="flex gap-2">
                      <Button onClick={() => handleVerifyStatus(payment.payment_id)} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                        Verificar Status
                      </Button>
                      {payment.status !== 'approved' && (
                        <Button variant="outline" onClick={onNewRecharge}>Nova recarga</Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RechargeOrders;